import React, { useState, useEffect } from 'react';
import { 
  Shield, Lock, Key, AlertTriangle, Clock, RefreshCw, 
  Check, X, Eye, EyeOff, Copy, Download, Users
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SecurityDashboard = ({ adminToken }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [securityData, setSecurityData] = useState(null);
  const [loginAttempts, setLoginAttempts] = useState(null);
  const [auditLog, setAuditLog] = useState(null);
  const [show2FASetup, setShow2FASetup] = useState(false);
  const [twoFASecret, setTwoFASecret] = useState(null);
  const [verifyCode, setVerifyCode] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);

  const fetchSecurityData = async () => {
    setIsLoading(true);
    try {
      const [dashboardRes, loginRes, auditRes] = await Promise.all([
        axios.get(`${API_URL}/admin/security/dashboard?admin_token=${adminToken}`),
        axios.get(`${API_URL}/admin/security/login-attempts?admin_token=${adminToken}&days=7`),
        axios.get(`${API_URL}/admin/security/audit-log?admin_token=${adminToken}&days=30`)
      ]);
      
      setSecurityData(dashboardRes.data);
      setLoginAttempts(loginRes.data);
      setAuditLog(auditRes.data);
    } catch (error) {
      toast.error('Failed to load security data');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (adminToken) {
      fetchSecurityData();
    }
  }, [adminToken]);

  const handleSetup2FA = async () => {
    try {
      const response = await axios.post(`${API_URL}/admin/security/setup-2fa?admin_token=${adminToken}`);
      setTwoFASecret(response.data);
      setShow2FASetup(true);
    } catch (error) {
      toast.error('Failed to setup 2FA');
    }
  };

  const handleVerify2FA = async () => {
    if (!verifyCode || verifyCode.length !== 6) {
      toast.error('Please enter a valid 6-digit code');
      return;
    }
    
    setIsVerifying(true);
    try {
      await axios.post(`${API_URL}/admin/security/verify-2fa-setup?admin_token=${adminToken}&code=${verifyCode}`);
      toast.success('2FA has been enabled!');
      setShow2FASetup(false);
      setTwoFASecret(null);
      setVerifyCode('');
      fetchSecurityData();
    } catch (error) {
      toast.error('Invalid code. Please try again.');
    } finally {
      setIsVerifying(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-2 border-stone-300 border-t-stone-900 rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Security Status Banner */}
      <div className={`p-4 rounded-xl border ${
        securityData?.security_status === 'healthy' 
          ? 'bg-green-50 border-green-200' 
          : 'bg-amber-50 border-amber-200'
      }`}>
        <div className="flex items-center gap-3">
          <Shield className={`w-6 h-6 ${
            securityData?.security_status === 'healthy' ? 'text-green-600' : 'text-amber-600'
          }`} />
          <div>
            <p className="font-medium text-stone-900">
              Security Status: {securityData?.security_status === 'healthy' ? 'Healthy' : 'Needs Attention'}
            </p>
            <p className="text-sm text-stone-500">
              Last login: {securityData?.last_login ? new Date(securityData.last_login).toLocaleString() : 'Never'}
              {securityData?.last_login_ip && ` from ${securityData.last_login_ip}`}
            </p>
          </div>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-stone-200 p-4">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="w-4 h-4 text-amber-500" />
            <span className="text-xs text-stone-500">Failed Logins (24h)</span>
          </div>
          <p className="text-2xl font-bold text-stone-900">{securityData?.metrics?.failed_logins_24h || 0}</p>
        </div>
        <div className="bg-white rounded-xl border border-stone-200 p-4">
          <div className="flex items-center gap-2 mb-2">
            <Clock className="w-4 h-4 text-blue-500" />
            <span className="text-xs text-stone-500">Rate Limits Hit</span>
          </div>
          <p className="text-2xl font-bold text-stone-900">{securityData?.metrics?.rate_limits_24h || 0}</p>
        </div>
        <div className="bg-white rounded-xl border border-stone-200 p-4">
          <div className="flex items-center gap-2 mb-2">
            <Shield className="w-4 h-4 text-red-500" />
            <span className="text-xs text-stone-500">Suspicious (7d)</span>
          </div>
          <p className="text-2xl font-bold text-stone-900">{securityData?.metrics?.suspicious_activities_7d || 0}</p>
        </div>
        <div className="bg-white rounded-xl border border-stone-200 p-4">
          <div className="flex items-center gap-2 mb-2">
            <Users className="w-4 h-4 text-green-500" />
            <span className="text-xs text-stone-500">Active Sessions</span>
          </div>
          <p className="text-2xl font-bold text-stone-900">{securityData?.metrics?.active_admin_sessions || 0}</p>
        </div>
      </div>

      {/* Two-Factor Authentication */}
      <div className="bg-white rounded-xl border border-stone-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Key className="w-5 h-5 text-stone-400" />
            <div>
              <h3 className="font-medium text-stone-900">Two-Factor Authentication</h3>
              <p className="text-sm text-stone-500">Add an extra layer of security to your admin account</p>
            </div>
          </div>
          {securityData?.['2fa_enabled'] ? (
            <span className="flex items-center gap-1 px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm">
              <Check className="w-4 h-4" />
              Enabled
            </span>
          ) : (
            <Button onClick={handleSetup2FA} className="bg-stone-900 hover:bg-stone-800">
              Enable 2FA
            </Button>
          )}
        </div>
      </div>

      {/* Login Attempts */}
      <div className="bg-white rounded-xl border border-stone-200 p-6">
        <h3 className="font-medium text-stone-900 mb-4 flex items-center gap-2">
          <Lock className="w-5 h-5 text-stone-400" />
          Recent Login Attempts
        </h3>
        <div className="grid md:grid-cols-2 gap-4 mb-4">
          <div className="p-3 bg-green-50 rounded-lg">
            <p className="text-2xl font-bold text-green-700">{loginAttempts?.successful_logins || 0}</p>
            <p className="text-sm text-green-600">Successful logins (7 days)</p>
          </div>
          <div className="p-3 bg-red-50 rounded-lg">
            <p className="text-2xl font-bold text-red-700">{loginAttempts?.failed_logins || 0}</p>
            <p className="text-sm text-red-600">Failed attempts (7 days)</p>
          </div>
        </div>
        
        {loginAttempts?.suspicious_ips?.length > 0 && (
          <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg mb-4">
            <p className="text-sm text-amber-700 font-medium">
              ⚠️ Suspicious IPs detected: {loginAttempts.suspicious_ips.join(', ')}
            </p>
          </div>
        )}
        
        <div className="space-y-2 max-h-48 overflow-y-auto">
          {loginAttempts?.recent_failed?.slice(0, 10).map((attempt, idx) => (
            <div key={idx} className="flex items-center justify-between py-2 border-b border-stone-100 last:border-0 text-sm">
              <div>
                <span className="text-stone-700">{attempt.details?.identifier || 'Unknown'}</span>
                <span className="text-stone-400 ml-2">({attempt.details?.ip})</span>
              </div>
              <span className="text-xs text-stone-400">
                {new Date(attempt.timestamp).toLocaleString()}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Audit Log */}
      <div className="bg-white rounded-xl border border-stone-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-medium text-stone-900 flex items-center gap-2">
            <Eye className="w-5 h-5 text-stone-400" />
            Admin Audit Log
          </h3>
          <Button variant="outline" size="sm" onClick={fetchSecurityData}>
            <RefreshCw className="w-4 h-4 mr-1" />
            Refresh
          </Button>
        </div>
        <div className="space-y-2 max-h-64 overflow-y-auto">
          {auditLog?.audit_entries?.slice(0, 20).map((entry, idx) => (
            <div key={idx} className="flex items-center justify-between py-2 border-b border-stone-100 last:border-0 text-sm">
              <div className="flex items-center gap-2">
                <span className="px-2 py-0.5 bg-stone-100 text-stone-600 rounded text-xs">
                  {entry.action || entry.type}
                </span>
                <span className="text-stone-700">{entry.admin || entry.details?.admin || 'System'}</span>
              </div>
              <span className="text-xs text-stone-400">
                {new Date(entry.timestamp).toLocaleString()}
              </span>
            </div>
          ))}
          {(!auditLog?.audit_entries || auditLog.audit_entries.length === 0) && (
            <p className="text-center text-stone-500 py-4">No audit entries yet</p>
          )}
        </div>
      </div>

      {/* Security Checklist */}
      <div className="bg-white rounded-xl border border-stone-200 p-6">
        <h3 className="font-medium text-stone-900 mb-4">Security Checklist</h3>
        <div className="space-y-3">
          {[
            { label: 'Two-Factor Authentication', enabled: securityData?.['2fa_enabled'] },
            { label: 'Rate Limiting Active', enabled: true },
            { label: 'Brute Force Protection', enabled: true },
            { label: 'Session Security (HttpOnly cookies)', enabled: true },
            { label: 'Input Sanitization', enabled: true },
            { label: 'Security Headers (CSP, XSS)', enabled: true },
            { label: 'Audit Logging', enabled: true },
          ].map((item, idx) => (
            <div key={idx} className="flex items-center gap-2">
              {item.enabled ? (
                <Check className="w-5 h-5 text-green-500" />
              ) : (
                <X className="w-5 h-5 text-stone-300" />
              )}
              <span className={item.enabled ? 'text-stone-700' : 'text-stone-400'}>
                {item.label}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* 2FA Setup Dialog */}
      <Dialog open={show2FASetup} onOpenChange={setShow2FASetup}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Setup Two-Factor Authentication</DialogTitle>
            <DialogDescription>
              Scan the QR code or enter the secret manually in your authenticator app
            </DialogDescription>
          </DialogHeader>
          
          {twoFASecret && (
            <div className="space-y-4 mt-4">
              <div className="p-4 bg-stone-50 rounded-lg">
                <p className="text-xs text-stone-500 mb-2">Secret Key (click to copy)</p>
                <button 
                  onClick={() => copyToClipboard(twoFASecret.secret)}
                  className="font-mono text-sm bg-white px-3 py-2 rounded border border-stone-200 hover:bg-stone-50 w-full text-left"
                >
                  {twoFASecret.secret}
                </button>
              </div>
              
              <div>
                <label className="text-sm text-stone-600 mb-2 block">
                  Enter the 6-digit code from your app to verify:
                </label>
                <Input
                  type="text"
                  value={verifyCode}
                  onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  className="text-center text-2xl tracking-widest"
                  maxLength={6}
                />
              </div>
              
              <Button 
                onClick={handleVerify2FA} 
                disabled={isVerifying || verifyCode.length !== 6}
                className="w-full bg-stone-900 hover:bg-stone-800"
              >
                {isVerifying ? 'Verifying...' : 'Verify and Enable 2FA'}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SecurityDashboard;
